import { useState } from 'react';
import { api, useApi, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Icon, Loading, Sheet, Status, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';

interface Rule { ruleId?: string; timeBasis: 'location_local' | 'utc'; daysOfWeek: number[]; startTime: string; endTime: string; validFrom: string | null; validUntil: string | null; exceptionDates: string[] }
interface Entry { id: string; name: string; kind: string; priority: number; playlistName: string; rules: Rule[]; expiresAt: string | null; targetLocationIds: string[]; targetScreenIds: string[]; targetTags: string[]; enabled: boolean; campaignId: string | null }
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function describeRule(r: Rule): string {
  const days = r.daysOfWeek.length === 7 ? 'Every day' : r.daysOfWeek.map((d) => DAYS[d - 1]).join(', ');
  const basis = r.timeBasis === 'utc' ? 'UTC (same instant everywhere)' : 'local time at each location';
  const overnight = r.endTime <= r.startTime ? ' (ends next day)' : '';
  return `${days} ${r.startTime}–${r.endTime}${overnight}, ${basis}`;
}

export function Schedule() {
  const org = useOrg();
  const toast = useToast();
  const { data, error, loading, reload } = useApi<{ schedules: Entry[] }>(orgPath(org, '/schedules'), [org.id]);
  const [creating, setCreating] = useState(false);
  async function disable(e: Entry) {
    try {
      await api('DELETE', orgPath(org, `/schedules/${e.id}`));
      toast(`“${e.name}” disabled. Publish to update screens.`);
      void reload();
    } catch (err) {
      toast((err as ApiFailure).message, 'danger');
    }
  }
  return (
    <>
      <div className="page-head">
        <div><h1>Schedule</h1><p>Priority: emergency › takeover › campaign › playlist › house fallback. Ties use the numeric priority.</p></div>
        {can(org, 'schedules.write') && <button className="btn btn-primary" onClick={() => setCreating(true)}><Icon name="plus" /> Add schedule</button>}
      </div>
      {error && <ErrorState error={error} retry={reload} />}
      {loading && !data && <Loading />}
      {data && !data.schedules.length && <div className="card"><Empty title="Nothing scheduled">Add a playlist schedule and a house fallback so screens never go blank.</Empty></div>}
      {data && !!data.schedules.length && (
        <div className="card table-wrap">
          <table className="table">
            <thead><tr><th>Name</th><th>Kind</th><th>Playlist</th><th>When</th><th>Targets</th><th /></tr></thead>
            <tbody>
              {data.schedules.map((e) => (
                <tr key={e.id}>
                  {/* Disabled rows keep full contrast (WCAG 1.4.3): state is a badge, not fading. */}
                  <td><strong>{e.name}</strong>{!e.enabled && <div><span className="badge plain">Disabled</span></div>}</td>
                  <td><Status value={e.kind === 'emergency' ? 'critical' : e.kind === 'fallback' ? undefined : 'info'} label={`${e.kind} · ${e.priority}`} /></td>
                  <td>{e.playlistName}</td>
                  <td className="subtle">{e.kind === 'fallback' && !e.rules.length ? 'Whenever nothing else is scheduled' : e.rules.map(describeRule).join('; ')}{e.expiresAt && <div>Expires {new Date(e.expiresAt).toLocaleString()}</div>}</td>
                  <td className="subtle">{!e.targetLocationIds.length && !e.targetScreenIds.length && !e.targetTags.length ? 'All screens' : [e.targetLocationIds.length && `${e.targetLocationIds.length} location(s)`, e.targetScreenIds.length && `${e.targetScreenIds.length} screen(s)`, e.targetTags.length && `tags: ${e.targetTags.join(', ')}`].filter(Boolean).join(' · ')}</td>
                  <td>{e.enabled && !e.campaignId && can(org, 'schedules.write') && <button className="btn btn-sm" onClick={() => void disable(e)}>Disable</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <CreateEntry onClose={() => setCreating(false)} onCreated={reload} />}
    </>
  );
}

function CreateEntry({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const playlists = useApi<{ playlists: { id: string; name: string }[] }>(orgPath(org, '/playlists'), [org.id]);
  const locations = useApi<{ locations: { id: string; name: string; timezone: string }[] }>(orgPath(org, '/locations'), [org.id]);
  const [form, setForm] = useState({ name: '', kind: 'playlist', priority: 10, playlistId: '', timeBasis: 'location_local' as Rule['timeBasis'], days: [1, 2, 3, 4, 5, 6, 7], start: '09:00', end: '21:00', locationIds: [] as string[], expiresAt: '' });
  const [err, setErr] = useState<string | null>(null);
  const needsRule = form.kind !== 'fallback' && form.kind !== 'emergency';
  async function save() {
    try {
      await api('POST', orgPath(org, '/schedules'), {
        name: form.name, kind: form.kind, priority: form.priority, playlistId: form.playlistId,
        rules: needsRule || form.kind === 'emergency' ? [{ timeBasis: form.timeBasis, daysOfWeek: form.days, startTime: form.start, endTime: form.end }] : [],
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
        target: { locationIds: form.locationIds, screenIds: [], tags: [] },
      });
      toast('Schedule saved. Review and publish to send it to screens.');
      onCreated();
      onClose();
    } catch (e) {
      setErr((e as ApiFailure).message);
    }
  }
  const set = (p: Partial<typeof form>) => setForm({ ...form, ...p });
  return (
    <Sheet title="Add schedule" onClose={onClose} footer={<button className="btn btn-primary" disabled={!form.name || !form.playlistId} onClick={() => void save()}>Save schedule</button>}>
      <div className="stack" style={{ gap: 14 }}>
        {err && <div className="alert danger" role="alert">{err}</div>}
        <div className="field"><label htmlFor="s-name">Name</label><input id="s-name" className="input" value={form.name} onChange={(e) => set({ name: e.target.value })} /></div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="s-kind">Type</label>
            <select id="s-kind" className="input" value={form.kind} onChange={(e) => set({ kind: e.target.value })}>
              <option value="playlist">Playlist</option><option value="takeover">Takeover (needs expiry)</option><option value="fallback">House fallback</option>
              {can(org, 'override.emergency') && <option value="emergency">Emergency override</option>}
            </select>
          </div>
          <div className="field" style={{ width: 120 }}><label htmlFor="s-pri">Priority</label><input id="s-pri" className="input" type="number" min={0} max={1000} value={form.priority} onChange={(e) => set({ priority: Number(e.target.value) })} /></div>
        </div>
        <div className="field">
          <label htmlFor="s-pl">Playlist</label>
          <select id="s-pl" className="input" value={form.playlistId} onChange={(e) => set({ playlistId: e.target.value })}>
            <option value="">Choose a playlist</option>
            {playlists.data?.playlists.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        {needsRule && (
          <>
            <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="label">Time basis</legend>
              <div className="segmented" role="group">
                <button type="button" aria-pressed={form.timeBasis === 'location_local'} onClick={() => set({ timeBasis: 'location_local' })}>Local time at each location</button>
                <button type="button" aria-pressed={form.timeBasis === 'utc'} onClick={() => set({ timeBasis: 'utc' })}>UTC everywhere</button>
              </div>
              <span className="hint">{form.timeBasis === 'location_local' ? '09:00 means 9 AM in Chicago and 9 AM in Phoenix — different instants.' : '15:00 UTC is 10 AM in Chicago (CDT) and 8 AM in Phoenix — the same instant.'}</span>
            </fieldset>
            <fieldset className="row" style={{ border: 0, padding: 0, margin: 0 }} aria-label="Days">
              {DAYS.map((d, i) => (
                <label key={d} className="row" style={{ gap: 4 }}><input type="checkbox" checked={form.days.includes(i + 1)} onChange={(e) => set({ days: e.target.checked ? [...form.days, i + 1].sort() : form.days.filter((x) => x !== i + 1) })} />{d}</label>
              ))}
            </fieldset>
            <div className="row">
              <div className="field"><label htmlFor="s-start">Start</label><input id="s-start" className="input" type="time" value={form.start} onChange={(e) => set({ start: e.target.value })} /></div>
              <div className="field"><label htmlFor="s-end">End</label><input id="s-end" className="input" type="time" value={form.end} onChange={(e) => set({ end: e.target.value })} /></div>
              {form.end <= form.start && <span className="badge info plain">Overnight: ends the next day</span>}
            </div>
            <span className="hint">Daylight-saving rule: a start time that doesn’t exist (spring forward) begins at the next valid minute; a repeated time (fall back) uses its first occurrence.</span>
          </>
        )}
        {(form.kind === 'takeover' || form.kind === 'emergency') && (
          <div className="field"><label htmlFor="s-exp">Expires</label><input id="s-exp" className="input" type="datetime-local" value={form.expiresAt} onChange={(e) => set({ expiresAt: e.target.value })} /><span className="hint">Players enforce expiry even while offline.</span></div>
        )}
        {form.kind === 'emergency' && <div className="alert warn">Emergency overrides are audited and time-limited. LumaCast is not a certified life-safety alerting system.</div>}
        <div className="field">
          <span className="label">Locations (none = all screens)</span>
          <div className="row">
            {locations.data?.locations.map((l) => (
              <label key={l.id} className="row" style={{ gap: 4 }}><input type="checkbox" checked={form.locationIds.includes(l.id)} onChange={(e) => set({ locationIds: e.target.checked ? [...form.locationIds, l.id] : form.locationIds.filter((x) => x !== l.id) })} />{l.name}</label>
            ))}
          </div>
        </div>
      </div>
    </Sheet>
  );
}
